import axios from "axios";
import { clearAuth } from "../authSlice";
import { store } from "../../../app/store";
import { showToastOutsideReact } from "../../../components/toast/ToastContext";

const LOCAL_STORAGE_BACKUP_KEY = "__ams_localStorageBackup__";
let hasHandledSessionExpiry = false;

type LocalStorageBackup = {
  userId: string | null;
  createdAt: number;
  data: Record<string, string>;
};

const safeJsonParse = <T,>(raw: string | null): T | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

const getAuthUserIdFromStorage = (): string | null => {
  const authData = safeJsonParse<{ userId?: string | null; cognitoId?: string | null }>(
    localStorage.getItem("authData")
  );
  return authData?.userId ?? authData?.cognitoId ?? null;
};

const snapshotLocalStorage = (): Record<string, string> => {
  const snapshot: Record<string, string> = {};
  for (let index = 0; index < localStorage.length; index++) {
    const key = localStorage.key(index);
    if (!key) continue;
    if (key === LOCAL_STORAGE_BACKUP_KEY) continue;
    const value = localStorage.getItem(key);
    if (value !== null) snapshot[key] = value;
  }
  return snapshot;
};

export const saveLocalStorageBackupForCurrentUser = (): void => {
  try {
    const userId = getAuthUserIdFromStorage();
    const backup: LocalStorageBackup = {
      userId,
      createdAt: Date.now(),
      data: snapshotLocalStorage(),
    };
    localStorage.setItem(LOCAL_STORAGE_BACKUP_KEY, JSON.stringify(backup));
  } catch (error) {
    console.error("Failed to save localStorage backup:", error);
  }
};

export const restoreLocalStorageBackupIfSameUser = (userId: string | null | undefined): void => {
  if (!userId) return;

  try {
    const backup = safeJsonParse<LocalStorageBackup>(localStorage.getItem(LOCAL_STORAGE_BACKUP_KEY));
    if (!backup?.userId) return;
    if (backup.userId !== userId) return;

    for (const [key, value] of Object.entries(backup.data ?? {})) {
      if (key === "authData") continue;
      localStorage.setItem(key, value);
    }
  } catch (error) {
    console.error("Failed to restore localStorage backup:", error);
  }
};

const clearLocalStoragePreservingBackup = (): void => {
  try {
    const keysToRemove: string[] = [];
    for (let index = 0; index < localStorage.length; index++) {
      const key = localStorage.key(index);
      if (!key) continue;
      if (key === LOCAL_STORAGE_BACKUP_KEY) continue;
      keysToRemove.push(key);
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    console.error("Failed to clear localStorage:", error);
  }
};

const apiBase = process.env.REACT_APP_API_URL || "";

export const forceLocalLogout = (options?: { redirectTo?: string }) => {
  try {
    saveLocalStorageBackupForCurrentUser();
  } catch (error) {
    console.error("Failed to save localStorage backup during forced logout:", error);
  }

  try {
    store.dispatch(clearAuth());
  } catch (error) {
    console.error("Failed to clear auth state during forced logout:", error);
  }

  try {
    clearLocalStoragePreservingBackup();
  } catch (error) {
    console.error("Failed to clear localStorage during forced logout:", error);
  }

  const target = options?.redirectTo ?? "/";
  window.location.href = target;
};

export const handleSessionExpiredLogout = (options?: {
  redirectTo?: string;
  redirectDelayMs?: number;
}): void => {
  if (hasHandledSessionExpiry) return;
  hasHandledSessionExpiry = true;

  showToastOutsideReact(
    "Session Expired",
    "Your session has ended. Please log in again to continue.",
    "error"
  );

  const delay = options?.redirectDelayMs ?? 2000;
  window.setTimeout(() => {
    forceLocalLogout({ redirectTo: options?.redirectTo });
  }, delay);
};

export const exchangeCodeForToken = async (
  code: string,
  domain: string,
  redirectUri: string
) => {
  if (!apiBase) {
    console.error("REACT_APP_API_URL is not configured for portal auth login.");
    return null;
  }

  try {
    const response = await axios.post(`${apiBase}/portal/auth/login`, {
      code,
      domain,
      redirectUri,
    });
    return response.data;
  } catch (error) {
    console.error("Error calling /portal/auth/login:", error);
    return null;
  }
};

export const refreshToken = async (): Promise<string | null> => {
  const storedAuthDataRaw = localStorage.getItem("authData");
  if (!storedAuthDataRaw) return null;

  const storedAuthData = JSON.parse(storedAuthDataRaw);
  const { refreshToken, domain } = storedAuthData;

  if (!refreshToken || !domain) {
    console.error("No refresh token or domain found.");
    return null;
  }

  if (!apiBase) {
    console.error("REACT_APP_API_URL is not configured for portal auth refresh.");
    return null;
  }

  try {
    const response = await axios.post(`${apiBase}/portal/auth/refresh`, {
      refreshToken,
      domain,
    });

    const data: any = response.data;
    const tokenPayload = data?.token || data;

    if (!tokenPayload?.accessToken || !tokenPayload?.expiresIn) {
      console.error("Unexpected refresh response shape:", data);
      return null;
    }

    const updatedAuthData = {
      ...storedAuthData,
      accessToken: tokenPayload.accessToken,
      refreshToken: tokenPayload.refreshToken || refreshToken,
      // expiresIn from backend is assumed to be seconds-from-now
      expiresIn: Date.now() + Number(tokenPayload.expiresIn) * 1000,
    };

    localStorage.setItem("authData", JSON.stringify(updatedAuthData));
    return tokenPayload.accessToken as string;
  } catch (error) {
    console.error("Token refresh error:", error);
    return null;
  }
};


export const getAccessToken = () => {
  const authData = JSON.parse(localStorage.getItem("authData") || '{}');
  return authData?.accessToken || null;
};
export const isAuthenticated = async (): Promise<boolean> => {
  const authData = JSON.parse(localStorage.getItem("authData") || '{}');
  const token = authData?.accessToken;
  const expiresAt = authData?.expiresIn;
  if (!token || !expiresAt) return false;
  const now = Date.now();
  if (now >= Number(expiresAt)) {
    const newToken = await refreshToken();
    if (!newToken) return false;
  }
  return true;
};
export const logOut = async () => {
  try {
    const storedAuthDataRaw = localStorage.getItem("authData");
    const storedAuthData = storedAuthDataRaw ? JSON.parse(storedAuthDataRaw) : null;
    const accessToken = storedAuthData?.accessToken as string | undefined;
    const domain = storedAuthData?.domain as string | undefined;

    if (apiBase && accessToken && domain) {
      try {
        await axios.post(`${apiBase}/portal/auth/logout`, {
          accessToken,
          domain,
        });
      } catch (error) {
        console.error("Error calling /portal/auth/logout (continuing logout locally):", error);
      }
    }
  } catch (e) {
    console.error("Failed during logout pre-processing:", e);
  }
  forceLocalLogout();
};
